package com.appsyde.shared.presentation.screen.component.ticket

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.app.presentation.ticket.buildActiveSegment
import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.model.parseKeysJsonToSet
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondState
import com.appsyde.shared.presentation.screen.component.SpacerXSmall
import com.appsyde.shared.presentation.screen.component.message.helper.intentLabelFr
import com.appsyde.shared.presentation.screen.component.message.helper.withHash
import com.appsyde.shared.presentation.screen.component.message.mapper.uiLabel
import com.appsyde.shared.presentation.screen.component.message.mapper.uiLabelForKey
import com.appsyde.shared.presentation.screen.component.message.mapper.uiValue
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.AppStylesControls
import com.appsyde.shared.presentation.style.style.AppStylesTickets
import com.appsyde.shared.presentation.style.style.AppStylesTicketsSidebar
import com.appsyde.shared.presentation.style.style.InsightStyles
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date
import kotlinx.coroutines.delay

private enum class AnalysisEmptyPhase { ANALYZING, READY_SHOWING, FADING_OUT, EMPTY }

/** "Alice Dupont" -> "AD", "Alice" -> "A" */
private fun initialsFromDisplayName(displayName: String): String {
    val parts = displayName.trim().split(" ").filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].first().uppercaseChar().toString()
        else -> "${parts[0].first().uppercaseChar()}${parts[1].first().uppercaseChar()}"
    }
}

/**
 * Sidebar pilotage décisionnel : État + action | Client | Mémoire (liste) | Blocages (compact).
 * L'action est collée au bloc État du dossier. Logique : WAITING → Relancer / OPEN+ready → Traiter remboursement / OPEN → Traiter ce ticket.
 */
@Composable
fun TicketsSidebar(
    respond: TicketRespondState?,
    extract: AiExtractV2? = null,
    extractAnalyzedCount: Int = 0,
    extractTotalCount: Int = 0,
    currentUserAccountId: String? = null,
    isOpeningForResponse: Boolean = false,
    onTraiterClick: () -> Unit,
    onReprendreClick: (() -> Unit)? = null,
    onPasserEnFerme: (() -> Unit)? = null,
    onTousSesTicketsClick: ((email: String) -> Unit)? = null
) {
    Style(AppStylesTicketsSidebar)
    Style(AppStylesTickets)
    Style(InsightStyles)
    Style(AppStylesControls)
    Style(AppStylesAnimations)

    var nowMs by remember { mutableStateOf(0L) }
    LaunchedEffect(Unit) { nowMs = Date.now().toLong() }

    if (respond == null) {
        val hasPendingExtract = extractTotalCount > 0 && extractAnalyzedCount < extractTotalCount
        var phase by remember { mutableStateOf(AnalysisEmptyPhase.EMPTY) }
        when {
            hasPendingExtract -> phase = AnalysisEmptyPhase.ANALYZING
            extractTotalCount == 0 -> phase = AnalysisEmptyPhase.EMPTY
            extractAnalyzedCount == extractTotalCount && phase == AnalysisEmptyPhase.ANALYZING -> phase = AnalysisEmptyPhase.READY_SHOWING
        }
        LaunchedEffect(phase) {
            when (phase) {
                AnalysisEmptyPhase.READY_SHOWING -> {
                    delay(1500)
                    phase = AnalysisEmptyPhase.FADING_OUT
                }
                AnalysisEmptyPhase.FADING_OUT -> {
                    delay(400)
                    phase = AnalysisEmptyPhase.EMPTY
                }
                else -> {}
            }
        }
        when (phase) {
            AnalysisEmptyPhase.ANALYZING -> {
                val inProgressCount = extractTotalCount - extractAnalyzedCount
                Div({ classes(InsightStyles.panelCard, AppStylesTicketsSidebar.analysisProgressCard) }) {
                    Div({ classes(AppStylesTicketsSidebar.analysisProgressHeader) }) {
                        Div({ classes(AppStylesTicketsSidebar.analysisProgressDot) }) {}
                        Div({ classes(AppStylesTicketsSidebar.analysisProgressTitle) }) {
                            Text("Analyse des conversations")
                        }
                    }
                    Div({ classes(AppStylesTickets.headerSub) }) {
                        Text("AppSyde extrait les informations utiles de vos emails pour construire le contexte des tickets.")
                    }
                    Div({ classes(AppStylesTicketsSidebar.analysisProgressCounts) }) {
                        Div({ classes(AppStylesTicketsSidebar.analysisProgressLineItem, AppStylesTicketsSidebar.analysisProgressLineItemReady) }) {
                            Text("✔ $extractAnalyzedCount contextes prêts")
                        }
                        Div({ classes(AppStylesTicketsSidebar.analysisProgressLineItem) }) {
                            Text("⟳ $inProgressCount en cours")
                        }
                    }

                }
            }
            AnalysisEmptyPhase.READY_SHOWING, AnalysisEmptyPhase.FADING_OUT -> {
                val fadeOut = phase == AnalysisEmptyPhase.FADING_OUT
                Div(attrs = {
                    classes(
                        listOfNotNull(
                            InsightStyles.panelCard,
                            AppStylesTicketsSidebar.readyCard,
                            if (fadeOut) AppStylesTicketsSidebar.readyCardFadeOut else null
                        )
                    )
                }) {
                    Div({ classes(AppStylesTicketsSidebar.readyCardTitle) }) { Text("Boîte prête") }
                    Div({ classes(AppStylesTicketsSidebar.analysisProgressLineItem, AppStylesTicketsSidebar.analysisProgressLineItemReady) }) {
                        Text("✔ $extractTotalCount / $extractTotalCount prêts")
                    }
                }
            }
            AnalysisEmptyPhase.EMPTY -> {
                Div(attrs = {
                    classes(InsightStyles.panelCard, AppStylesTicketsSidebar.noSelectionCard, AppStylesTicketsSidebar.noSelectionCardFadeIn)
                }) {
                    Div({ classes(AppStylesTicketsSidebar.noSelectionTitle) }) {
                        Text("Aucun ticket sélectionné")
                    }
                    Div({ classes(AppStylesTicketsSidebar.noSelectionSubtitle) }) {
                        Text("Sélectionnez une conversation pour afficher son contexte.")
                    }
                }
            }
        }
        return
    }

    val ticket = respond.ticket
    val segment = buildActiveSegment(respond)
    val lastMsg = segment.messages.lastOrNull()
    val lastFromUs = lastMsg?.direction == Direction.OUTBOUND
    // Même référence que la row : lastInboundSentAtMs (ou updatedAtMs) pour que la durée affichée soit identique
    val lastActivityAge = formatTicketAge(ticket.lastInboundSentAtMs ?: ticket.updatedAtMs, nowMs)
    val lastActivityWho = when (lastMsg?.direction) {
        Direction.INBOUND -> "Client"
        Direction.OUTBOUND -> "Vous"
        else -> null
    }

    val statusNorm = ticket.status.uppercase().takeIf { it in setOf(TicketStatus.OPEN, TicketStatus.WAITING, TicketStatus.CLOSED) } ?: ticket.status
    val statusLabel = when (statusNorm) {
        TicketStatus.OPEN -> "À traiter"
        TicketStatus.WAITING -> "En attente client"
        TicketStatus.CLOSED -> "Fermé"
        else -> ticket.status
    }
    val stateDotClass = when (statusNorm) {
        TicketStatus.OPEN -> AppStylesTicketsSidebar.stateDotOpen
        TicketStatus.WAITING -> AppStylesTicketsSidebar.stateDotWaiting
        else -> AppStylesTicketsSidebar.stateDotClosed
    }

    val missingSet = parseKeysJsonToSet(ticket.missingKeysJson).map { it.trim().lowercase() }.filter { it.isNotBlank() }.sorted()
    val toConfirmSet = parseKeysJsonToSet(ticket.toConfirmKeysJson).map { it.trim().lowercase() }.filter { it.isNotBlank() }.sorted()
    val missingKeysNorm = missingSet.toSet()

    val (operationalStateLabel, operationalStateKind) = computeOperationalState(
        statusNorm = statusNorm,
        missingKeysList = missingSet,
        lastFromUs = lastFromUs,
        normalizedIntent = extract?.intent?.trim()?.lowercase()?.replace(" ", "_")?.takeIf { it.isNotBlank() }
            ?: ticket.currentIntent?.trim()?.lowercase()?.replace(" ", "_")?.takeIf { it.isNotBlank() }
    )
    val opStatePillClass = when (operationalStateKind) {
        "waiting" -> AppStylesTickets.opStatePillWaiting
        "ready" -> AppStylesTickets.opStatePillReady
        "refund" -> AppStylesTickets.opStatePillRefund
        "closed" -> AppStylesTickets.opStatePillClosed
        else -> AppStylesTickets.opStatePillAction
    }

    val detectedFields = com.appsyde.shared.app.presentation.ticket.TicketDetectedFromMemory.parseAndEnrich(
        ticket.detectedMemoryJson,
        ticket.clientEmail
    )

    val clientEmail = ticket.clientEmail?.trim()?.takeIf { it.isNotBlank() }
    val clientName = detectedFields.firstOrNull { it.key?.trim()?.lowercase() == "customer_name" }?.value?.trim()?.takeIf { it.isNotBlank() }

    val memoryKeysExcluded = setOf("customer_email", "email", "customer_name")
    val memoryFields = detectedFields.filter { it.key?.trim()?.lowercase() !in memoryKeysExcluded }
    val detectedKeys = memoryFields.mapNotNull { it.key?.trim()?.lowercase() }.filter { it.isNotBlank() }.toSet()
    val missingNotInDetected = missingSet.filter { it !in detectedKeys }

    val mainActionLabel = when {
        statusNorm == TicketStatus.CLOSED -> "Voir ce ticket"
        statusNorm == TicketStatus.WAITING -> "Relancer le client"
        operationalStateKind == "ready" || operationalStateKind == "refund" -> "Traiter le remboursement"
        else -> "Traiter ce ticket"
    }

    // Éviter le flash "subject" : tant que extract n’est pas chargé, ne pas utiliser subject (on affiche "Dossier" puis le bon titre à l’arrivée de l’extract).
    val headerTitle = intentLabelFr(extract?.intent ?: ticket.currentIntent)
        ?: (extract?.let { ticket.subject?.trim()?.takeIf { s -> s.isNotBlank() } })
        ?: "Dossier"
    val orderValue = memoryFields
        .firstOrNull { it.key?.trim()?.lowercase() in setOf("order_number", "order_id") }
        ?.uiValue()?.trim()?.takeIf { it.isNotBlank() }
    val headerSubtitle = orderValue?.let { "Commande ${withHash(it)}" }

    val productKeys = setOf("product_name", "missing_product_name")
    val memoryDisplayRows = buildList {
        val productValues = mutableListOf<String>()
        var productAnyMissing = false
        memoryFields.forEach { d ->
            val key = d.key?.trim()?.lowercase().orEmpty()
            val label = when (key) {
                "issue_type" -> "Problème"
                "refund_preference" -> "Préférence"
                "order_number", "order_id" -> "Commande"
                "product_name", "missing_product_name" -> "Produit"
                "photo_proof" -> "Preuve photo"
                "delivery_proof" -> "Preuve livraison"
                else -> d.uiLabel().takeIf { it.isNotBlank() } ?: key
            }
            if (label.isBlank()) return@forEach
            if (key in productKeys) {
                if (key in missingKeysNorm) productAnyMissing = true
                else productValues.add(d.uiValue())
            } else {
                val isMissing = key in missingKeysNorm
                add(Triple(label, if (isMissing) "Non fournie" else d.uiValue(), isMissing))
            }
        }
        if (productValues.isNotEmpty() || productAnyMissing) {
            val value = if (productAnyMissing && productValues.isEmpty()) "Non fournie"
            else if (productAnyMissing) productValues.joinToString(", ") + " • Non fournie"
            else productValues.joinToString(", ")
            add(Triple(if (productValues.size > 1 || productAnyMissing) "Produit(s)" else "Produit", value, productAnyMissing))
        }
        missingNotInDetected.forEach { key ->
            val label = uiLabelForKey(key) ?: key
            add(Triple(label, "Non fournie", true))
        }
    }

    val allAttachments = segment.messages.flatMap { it.attachments }
    val attachmentCount = allAttachments.size
    val attachmentSummary = if (attachmentCount == 0) null else {
        fun typeLabel(mime: String?): String = when {
            mime == null -> "fichier"
            mime.startsWith("image/") -> "image"
            mime == "application/pdf" -> "pdf"
            mime.startsWith("application/") -> "document"
            mime.startsWith("text/") -> "texte"
            else -> "fichier"
        }
        allAttachments
            .groupBy { typeLabel(it.mimeType) }
            .mapValues { it.value.size }
            .toList()
            .sortedBy { -it.second }
            .joinToString(", ") { "${it.first} (${it.second})" }
    }

    // ---------- Une seule card : en-tête · État · Client · Données · Bouton ----------
    Div({ classes(InsightStyles.panelCard, AppStylesTicketsSidebar.dossierCardSingle) }) {
        // 1) En-tête : titre + cercle initiales assignee à droite
        Div({ classes(AppStylesTicketsSidebar.dossierHeaderBlock) }) {
            Div({ classes(AppStylesTicketsSidebar.dossierHeaderTitleRow) }) {
                Div({ classes(AppStylesTicketsSidebar.dossierHeaderTitleFlex) }) {
                    Div({ classes(AppStylesTicketsSidebar.dossierHeaderTitle) }) { Text(headerTitle) }
                    if (headerSubtitle != null) {
                        Div({ classes(AppStylesTicketsSidebar.dossierHeaderSubtitle) }) { Text(headerSubtitle) }
                    }
                }
                if (ticket.assignedTo != null) {
                    val assigneeName = ticket.assignedTo.displayName.ifBlank { "—" }
                    Div(attrs = {
                        classes(AppStylesTicketsSidebar.dossierHeaderAssigneeCircle)
                        attr("title", assigneeName)
                    }) {
                        Span({ classes(AppStylesTicketsSidebar.dossierHeaderAssigneeCircleText) }) {
                            Text(initialsFromDisplayName(assigneeName))
                        }
                    }
                }
            }
        }

        // 2) Ligne statut : [dot] À traiter / En attente client · date
        Div({ classes(AppStylesTicketsSidebar.dossierStateSection) }) {
            Div({ classes(AppStylesTicketsSidebar.stateRowWithDate) }) {
                Div({ classes(AppStylesTicketsSidebar.stateRow) }) {
                    Div({ classes(stateDotClass) }) {}
                    Div({ classes(AppStylesTicketsSidebar.stateText) }) { Text(statusLabel) }
                }
                if (lastActivityAge.isNotBlank()) {
                    Div({ classes(AppStylesTicketsSidebar.stateDateRight) }) { Text(lastActivityAge) }
                }
            }
        }

        // 3) Client — un peu plus proche du statut, sans fond ni bordure
        if (clientEmail != null || clientName != null) {
            Div({ classes(AppStylesTicketsSidebar.dossierSectionClient) }) {
                Div({ classes(AppStylesTicketsSidebar.dossierSubtitle) }) { Text("Client") }
                Div({ classes(AppStylesTicketsSidebar.clientInfoGroup) }) {
                    if (clientName != null) {
                        Div({ classes(AppStylesTicketsSidebar.clientCardNameLight) }) { Text(clientName) }
                    }
                    if (clientEmail != null) {
                        Div({ classes(AppStylesTicketsSidebar.clientCardEmailLight) }) { Text(clientEmail) }
                    }
                }
                if (clientEmail != null && onTousSesTicketsClick != null) {
                    Button(attrs = {
                        classes(AppStylesTicketsSidebar.clientLinkLight)
                        attr("type", "button")
                        onClick { onTousSesTicketsClick(clientEmail) }
                    }) { Text("Tous ses tickets") }
                } else {
                    Div({ classes(AppStylesTicketsSidebar.clientLinkLight) }) { Text("Tous ses tickets") }
                }
            }
        }

        // 4) Résumé
        val summaryText = ticket.respondContext?.takeIf { it.isNotBlank() }
            ?: extract?.respondContext?.takeIf { it.isNotBlank() }
        Div({ classes(AppStylesTicketsSidebar.dossierSummarySection) }) {
            Div({ classes(AppStylesTicketsSidebar.dossierSubtitle) }) { Text("Résumé") }
            Div({ classes(if (summaryText.isNullOrBlank()) AppStylesTicketsSidebar.dossierSummaryTextEmpty else AppStylesTicketsSidebar.dossierSummaryText) }) {
                Text(summaryText ?: "Aucun résumé pour l'instant")
            }
        }

        val hasBlockages = missingSet.isNotEmpty() || toConfirmSet.isNotEmpty()

        // 5) À compléter (si existe)
        if (hasBlockages) {
            Div({ classes(AppStylesTicketsSidebar.completeCardInline) }) {
                Div({ classes(AppStylesTicketsSidebar.completeCardTitle) }) { Text("À compléter") }
                Div({ classes(AppStylesTicketsSidebar.completeChecklist) }) {
                    missingSet.forEach { key ->
                        val label = uiLabelForKey(key) ?: key
                        Div({ classes(AppStylesTicketsSidebar.completeCheckItem) }) {
                            Div({ classes(AppStylesTicketsSidebar.completeCheckIconMissing) }) {}
                            Text("$label requis")
                        }
                    }
                    toConfirmSet.forEach { key ->
                        val label = uiLabelForKey(key) ?: key
                        Div({ classes(AppStylesTicketsSidebar.completeCheckItem) }) {
                            Div({ classes(AppStylesTicketsSidebar.completeCheckIconToConfirm) }) {}
                            Text("$label à confirmer")
                        }
                    }
                }
            }
        }

        Div({ classes(AppStylesTicketsSidebar.dossierSpacer) }) {}

        // 6) Bouton(s) — footer (si assigné à quelqu'un d'autre : label + Reprendre)
        val isAssignedToOther = ticket.assignedTo != null &&
            currentUserAccountId != null &&
            ticket.assignedTo.accountUserId != currentUserAccountId
        val assigneeDisplayName = ticket.assignedTo?.displayName?.takeIf { it.isNotBlank() } ?: "—"
        Div({ classes(AppStylesTicketsSidebar.stateBlockCtaWrap) }) {
            if (isAssignedToOther && onReprendreClick != null) {
                Div({ classes(AppStylesTicketsSidebar.takeoverLabel) }) {
                    Text("En cours chez $assigneeDisplayName")
                }
                Button(attrs = {
                    classes(AppStylesTicketsSidebar.btnReprendre)
                    onClick { onReprendreClick() }
                }) { Text("Reprendre") }
            } else {
                Button(attrs = {
                    classes(AppStylesControls.btnPrimary)
                    if (isOpeningForResponse) attr("disabled", "true")
                    else onClick { onTraiterClick() }
                }) {
                    if (isOpeningForResponse) {
                        Span({ classes(AppStylesTicketsSidebar.ctaButtonContent) }) {
                            Div({ classes(AppStylesAnimations.ctaSpinner) }) {}
                            Text(mainActionLabel)
                        }
                    } else {
                        Text(mainActionLabel)
                    }
                }
                if (statusNorm == TicketStatus.WAITING && onPasserEnFerme != null) {
                    Button(attrs = {
                        classes(AppStylesTicketsSidebar.ctaSecondary)
                        onClick { onPasserEnFerme() }
                    }) { Text("Passer en fermé") }
                }
            }
        }
    }
}
